import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  loadClassroomsList,
  clearClassroom,
  deleteMultipleClassrooms,
} from "../../../actions/admin";
import PropTypes from "prop-types";

import { usePersistedTableState } from "../../../hooks/usePersistedTableState";
import ClassroomsList from "./ClassroomsList";
import DeleteConfirmationModal from "../../layouts/DeleteConfirmationModal";

const Classrooms = ({
  loadClassroomsList,
  classroomsList,
  deleteMultipleClassrooms,
  classroomListLoading,
  clearClassroom,
  user,
  schoolYears,
  schoolsList,
}) => {
  const hist = useHistory();
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [showAppliedEffect, setShowAppliedEffect] = useState(false); // UI only
  const [isFirstClassroomsLoad, setIsFirstClassroomsLoad] = useState(
    () => !sessionStorage.getItem("classroomsPageLoadedOnce"),
  );
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const defaultFilterState = {
    Years: [],
    SchoolIDs: [],
    GradeIDs: [],
  };
  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    persistentFilters,
    updateFilter,
    setFilters,
  } = usePersistedTableState("classrooms", defaultFilterState);
  const [filterData, setFilterData] = useState(
    () => persistentFilters || defaultFilterState,
  );
  const hasActiveFilters = (filters) =>
    Object.values(filters).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  const isAssessmentLoaded = useRef(false);
  const isFilterEffectFirstRun = useRef(true);
  const currentYear = schoolYears?.endYear || new Date().getFullYear() + 1;

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilterData(persistentFilters);
    }
  }, [persistentFilters]);

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilters(filterData);
    }
  }, [filterData, persistentFilters, setFilters]);
  const getFirstSchoolID = () => {
    const schoolIds = user?.schoolIds
      ? String(user.schoolIds)
          .split(",")
          .map((id) => id.trim())
      : [];

    if (schoolIds.length > 0 && schoolIds[0]) {
      return [Number(schoolIds[0])];
    }

    return user?.SchoolID ? [user.SchoolID] : [];
  };
  useEffect(() => {
    const firstSchoolID = getFirstSchoolID();
    if (
      firstSchoolID.length === 0 &&
      (!schoolsList || schoolsList.length === 0)
    ) {
      return;
    }
    // Prevent duplicate API calls on same page load
    if (isAssessmentLoaded.current) {
      return;
    }
    // const defaultSchools =
    //   firstSchoolID.length > 0
    //     ? firstSchoolID
    //     : schoolsList?.length > 0
    //       ? [schoolsList[0].SchoolID]
    //       : [];
    isAssessmentLoaded.current = true;
    const defaultYears = [currentYear];
    setFilterData((prev) => ({
      ...prev,
      // SchoolIDs: prev.SchoolIDs.length > 0 ? prev.SchoolIDs : defaultSchools,
      Years:
        Array.isArray(prev?.Years) && prev.Years.length > 0
          ? prev.Years
          : defaultYears,
    }));

    // Dispatch initial fetch with the first school default
    // loadClassroomsList({
    //   ...filterData,
    //   // SchoolIDs: defaultSchools,
    //   Years: defaultYears,
    // });
  }, [user?.schoolIds, user?.SchoolID, schoolsList]); // Syncs when auth state loads
  // useEffect(() => {
  //   if (classroomsList.length === 0 && classroomListLoading && user !== null) {
  //     loadClassroomsList();
  //   }
  // }, [classroomsList, classroomListLoading, loadClassroomsList, user]);
  useEffect(() => {
    if (!hasActiveFilters(filterData)) return;

    const shouldShowFilterLoading = !isFilterEffectFirstRun.current;
    if (isFilterEffectFirstRun.current) {
      isFilterEffectFirstRun.current = false;
    }

    if (shouldShowFilterLoading) {
      setIsFilterLoading(true);
    }

    let isActive = true;
    const timeoutId = setTimeout(() => {
      loadClassroomsList({
        ...filterData,
      }).finally(() => {
        if (isActive && shouldShowFilterLoading) {
          setIsFilterLoading(false);
        }
      });
    }, 1000);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [filterData]);

  useEffect(() => {
    if (!classroomListLoading && isFirstClassroomsLoad) {
      setIsFirstClassroomsLoad(false);
      sessionStorage.setItem("classroomsPageLoadedOnce", "true");
    }
  }, [classroomListLoading, isFirstClassroomsLoad]);

  const handleApplyFilter = () => {
    const payload = {
      ...filterData,
      // Fallback to user default schools if no explicit school selected in filter UI
      SchoolIDs:
        filterData.SchoolIDs.length > 0
          ? filterData.SchoolIDs
          : getFirstSchoolID(),
    };
    setFiltersApplied(true);
    setIsFilterLoading(true);
    loadClassroomsList(payload).finally(() => setIsFilterLoading(false));
    // Temporary success effect
    setShowAppliedEffect(true);

    setTimeout(() => {
      setShowAppliedEffect(false);
    }, 1000);
  };
  const handleClearFilter = () => {
    const firstSchoolID = getFirstSchoolID();
    const defaultYears = [currentYear];
    setFilterData({
      SchoolIDs: [],
      Years: defaultYears,
      GradeIDs: [],
    });
    setFiltersApplied(false);
  };

  return (
    <div className="p-sm-5 p-2 w-100 dashboard-margin">
      <div className="mb-3 ">
        <div className="d-flex align-items-center">
          <h6 className="txt-primary-light mb-0">
            {user.UserType} / Classrooms
          </h6>{" "}
          <div className="rounded-pill bg-primary px-2 py-1 align-self-center mx-2 my-2 caption ">
            {user.UserTypeID === 1
              ? classroomsList.filter(
                  (item) =>
                    item.TeacherID === user.UserID ||
                    item.SecondaryTeacherID === user.UserID,
                ).length
              : classroomsList.length}
          </div>
        </div>
        <div className="d-flex w-100 align-items-center justify-content-end">
          <div className="d-flex">
            {selectedClassrooms.length > 0 && (
              <button
                className="btn btn-danger d-flex align-items-center px-4 me-3"
                data-bs-toggle="modal"
                data-bs-target="#deleteMultipleClassrooms"
                // onClick={() => handleBulkDelete(selectedSchools)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-trash-fill button-child me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5z" />
                </svg>
                Delete ({selectedClassrooms.length})
              </button>
            )}
            {parseInt(user.UserTypeID) !== 1 && (
              <div
                className="btn button-parent button-primary d-flex align-items-center px-3"
                onClick={() => {
                  clearClassroom();
                  hist.push("/admin/classroom/add");
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  enableBackground="new 0 0 24 24"
                  height="18px"
                  viewBox="0 0 24 24"
                  width="24px"
                  fill="#ffffff"
                  className="button-child"
                >
                  <g>
                    <rect fill="none" height="18" width="18" />
                  </g>
                  <g>
                    <g>
                      <path d="M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6V13z" />
                    </g>
                  </g>
                </svg>
                Add Classroom
              </div>
            )}
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        id="deleteMultipleClassrooms"
        title="Delete Classroom(s)"
        message="Are you sure you want to delete Classroom(s)?"
        warning="Deleting a Classroom will result in deleting everything related to it such as assessments or results."
        onConfirm={() => {
          // deleteSchool(SchoolID);
          // hist.push("/admin/schools");
          deleteMultipleClassrooms(selectedClassrooms, filterData);

          setSelectedClassrooms([]);
          // clearSchool();
        }}
      />
      <ClassroomsList
        classroomsList={
          user.UserTypeID === 1
            ? classroomsList.filter(
                (item) =>
                  item.TeacherID === user.UserID ||
                  item.SecondaryTeacherID === user.UserID,
              )
            : classroomsList
        }
        classroomListLoading={classroomListLoading}
        showLoader={
          (classroomListLoading && isFirstClassroomsLoad) || isFilterLoading
        }
        user={user}
        schoolYears={schoolYears}
        deleteMultipleClassrooms={deleteMultipleClassrooms}
        filterData={filterData}
        setFilterData={setFilterData}
        handleApplyFilter={handleApplyFilter}
        handleClearFilter={handleClearFilter}
        filtersApplied={filtersApplied}
        showAppliedEffect={showAppliedEffect}
        selectedClassrooms={selectedClassrooms}
        setSelectedClassrooms={setSelectedClassrooms}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        page={page}
        setPage={setPage}
      />
    </div>
  );
};

Classrooms.propTypes = {
  classroomsList: PropTypes.array,
  loadClassroomsList: PropTypes.func.isRequired,
  deleteMultipleClassrooms: PropTypes.func.isRequired,
  classroomListLoading: PropTypes.bool,
  clearClassroom: PropTypes.func.isRequired,
  user: PropTypes.object,
};
const mapStateToProps = (state) => ({
  classroomsList: state.admin.classroomsList,
  classroomListLoading: state.admin.classroomListLoading,
  user: state.auth.user,
  schoolYears: state.admin.schoolYears,
  schoolsList: state.admin.schoolsList,
});

export default connect(mapStateToProps, {
  clearClassroom,
  loadClassroomsList,
  deleteMultipleClassrooms,
})(Classrooms);
